import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Users, Settings, Cloud } from "lucide-react";

const CustomPrice = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-50 to-indigo-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Enterprise-Grade Digital Transformation, Without the Enterprise Price Tag
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Harness the power of cutting-edge technology without the high costs, risks, or long implementation times. At Dynamico, we keep it simple and efficient.
          </p>
        </div>
      </section>

      {/* Flexible Pricing Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Flexible Pricing Tailored to Your Needs
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Whether you're a large enterprise or a growing business, our digital transformation platform is designed to be cost-effective and easy to implement for all organizations.
          </p>
        </div>
      </section>

      {/* Split Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Get in touch for a quote - starting from $50 per user or $400 per use case
              </h2>
              <div className="space-y-4 text-gray-600">
                <p className="text-gray-600 leading-relaxed">
                  Our highly configurable, no-code platform seamlessly integrates with your existing systems, eliminating the need for complex programming. Deploy in days, not months – and pay only for what you use.
                </p>
                <div className="mt-6">
                  <h3 className="text-xl font-semibold mb-4">Why Choose Dynamico?</h3>
                  <ul className="space-y-3">
                    <li>
                      <strong>White Label:</strong> Customize with your company logo and publish Android and iOS apps under your brand.
                    </li>
                    <li>
                      <strong>Build Your Case:</strong> Get expert business support to tailor solutions to your needs.
                    </li>
                    <li>
                      <strong>24/7 Support:</strong> Our team is always available to assist you.
                    </li>
                    <li>
                      <strong>Flexible Hosting:</strong> Choose between on-premise hosting or cloud solutions.
                    </li>
                    <li>
                      <strong>Rapid Implementation:</strong> Get up and running quickly with minimal disruption.
                    </li>
                    <li>
                      <strong>Intuitive Interface:</strong> Easy to learn, reducing training and change management costs.
                    </li>
                    <li>
                      <strong>Proven Efficiency:</strong> Leverage industry-leading innovations developed with digital transformation pioneers.
                    </li>
                    <li>
                      <strong>Scalable & Secure:</strong> Built on the latest cloud technologies, ensuring top-tier security and reliability.
                    </li>
                  </ul>
                </div>
                <p className="mt-6 font-medium">
                  Start transforming your business today. <Link to="/request-demo" className="text-primary hover:underline">Contact us for a personalized quote!</Link>
                </p>
              </div>
            </div>
            <div className="relative h-96">
              <img
                src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d"
                alt="Person working on laptop showing business analytics"
                className="w-full h-full object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* New Technology Innovation Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Innovation That Drives Your Digital Future
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              At Dynamico, we're committed to providing cutting-edge solutions that help organizations thrive in the digital age. Our platform combines innovation with practicality, ensuring you stay ahead of the competition.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Team Collaboration</h3>
                <p className="text-gray-600">Enable seamless communication and workflow management across your organization.</p>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Easy Configuration</h3>
                <p className="text-gray-600">Customize and adapt the platform to match your specific business requirements.</p>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Cloud className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Cloud-Ready</h3>
                <p className="text-gray-600">Deploy and scale your solutions with confidence using our cloud-native architecture.</p>
              </div>
            </div>
            <div className="mt-12">
              <Link to="/request-demo">
                <Button size="lg" className="bg-primary text-white">
                  Start Your Digital Journey Today
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomPrice;
